import { applicationStarted } from 'application/model'
import { combine, forward, guard, sample } from 'effector'
import {
  $cars,
  $carsStageGate,
  $failed,
  $fetching,
  $filterWithDefaultValue,
  $firstLoadPage,
  $isLoadMoreCars,
  $maxPrice,
  $minPrice,
  $pagesLoaded,
  $relatedFilter,
  $settingsRelatedFilter,
  $sorting,
  $totalCount,
  DEFAULT_SORTING,
  fetchCarsOnListFx,
  getPageFromURLFx,
  getSortingFromUrlFx,
  handleIsLoadMoreCars,
  loadNextPage,
  nextSort,
  propagateToUrlFx,
  setFilterWithDefaultValue,
  setFirstLoadPage,
  setRelatedFilter,
  setSettingsRelatedFilter,
  setSort
} from 'features/cars-list/model/units'
import { $areCitiesSelected, $selectedCities } from 'features/city-select/model'
import { $declaration, $defaultFilterValues, $filterValues, $permanentFilterValues } from 'features/filter/model'
import { getProjectName } from 'lib/get-project-name'

import { FetchCarsFxProps } from '../../../api/cars'
import {
  $chosenCreditFilterRequestData,
  $creditFilterCountRequestData,
  fetchCarsOnCreditFormFx
} from '../../credit-filter-form/model'

$pagesLoaded.on(loadNextPage, (page) => page + 1)

$fetching.on(fetchCarsOnListFx.pending, (_, pending) => pending)

$cars.on(fetchCarsOnListFx.doneData, (_, data) => data.cars)

$totalCount.on(fetchCarsOnListFx.doneData, (_, data) => data.total_count)

$minPrice.on(fetchCarsOnListFx.doneData, (_, data) => data.minPrice)
$maxPrice.on(fetchCarsOnListFx.doneData, (_, data) => data.maxPrice)

$failed.on(fetchCarsOnListFx.fail, () => {
  console.error('fail')
  return true
})

/**
 * После загрузки стейджа запускаем забор параметров сортировки из УРЛ
 */
sample({
  source: $carsStageGate.state,
  clock: $carsStageGate.open,
  fn: (_, state) => {
    return state.carsListProps.sorting?.options.find((option) => option.active)
  },
  target: getSortingFromUrlFx
})

/**
 * При запуске приложения забираем из УРЛ текущую страницу
 */
forward({
  from: applicationStarted,
  to: getPageFromURLFx
})

/**
 * Когда забор завершён, отправляем результат в стор сортировки
 */
forward({
  from: getSortingFromUrlFx.doneData,
  to: $sorting
})

/**
 * Берём текущую страницу из УРЛ
 */
forward({
  from: getPageFromURLFx.doneData,
  to: $pagesLoaded
})

/**
 * При запросе на сортировку по ключу - включаем сортировку по ключу, по возрастанию.
 * Если она уже была включена для этого ключа - меняем сортировку с возрастания на убывание,
 * а если уже было по убыванию - отключаем сортировку вовсе
 */

$sorting.on(nextSort, (sorting, nextSort) => {
  if (!sorting) return DEFAULT_SORTING

  const { sort, order } = sorting

  if (getProjectName() === 'generic_v2') {
    const nextOrder = nextSort.order || 'asc'

    if (sort === nextSort.sort && order === nextOrder) {
      return DEFAULT_SORTING
    } else {
      return {
        sort: nextSort.sort,
        order: nextOrder
      }
    }
  } else {
    // Оригинальная логика для других проектов
    if (nextSort.sort === sort && order === 'desc') return DEFAULT_SORTING

    if (sort !== nextSort.sort) {
      return {
        sort: nextSort.sort,
        order: 'asc'
      }
    } else {
      return {
        sort,
        order: order === 'asc' ? 'desc' : 'desc'
      }
    }
  }
})

/**
 * Запросы на сортировку для АСП
 */
$sorting.on(setSort, (sorting, nextSort) => {
  let order = 'asc' as any
  if (nextSort.indexOf('_desc') >= 0) {
    order = 'desc'
  }
  let sort = nextSort.replace('_asc', '').replace('_desc', '')
  return {
    order,
    sort
  }
})

/**
 * Собираем всё, что нужно запросу и запускаем его, когда значения фильтра готовы, sorting выяснен, а гейт списка авто открыт
 */
export const $listRequestData = combine<FetchCarsFxProps>({
  appTheme: $carsStageGate.state.map((state) => state.appTheme),
  pageLength: $carsStageGate.state.map((state) => state.carsListProps?.pageLength || 25),
  carViewSettings: $carsStageGate.state.map((state) => state.carsListProps?.carViewSettings),
  baseUrl: $carsStageGate.state.map((state) => state.carsListProps?.getUrl),
  sorting: $sorting,
  filter: $filterValues,
  defaultFilterValues: $defaultFilterValues.state,
  permanentFilterValues: $permanentFilterValues.state,
  pagesLoaded: $pagesLoaded,
  city: $selectedCities,
  creditFilter: $chosenCreditFilterRequestData,
  firstLoadPage: $firstLoadPage,
  filterWithDefaultValue: $filterWithDefaultValue,
  filterDeclaration: $declaration,
  settingsRelatedFilter: $settingsRelatedFilter,
  showCarsNextDealer: $isLoadMoreCars.map((isLoadMoreCars) => isLoadMoreCars.isChanged)
})

export const $requestPossible = combine(
  $listRequestData,
  $carsStageGate.state,
  $sorting,
  $areCitiesSelected,
  $filterValues,
  ({ filter }, gate, sorting, areCitiesSelected) => {
    const citiesAreReady = !gate?.carsListProps?.citySelect || areCitiesSelected
    return !!gate && !!filter && citiesAreReady && !!sorting
  }
)

guard({
  clock: $listRequestData,
  filter: $requestPossible,
  target: fetchCarsOnListFx
})

////////////////////////////////////////////////////
export const countListRequestData = combine<FetchCarsFxProps>({
  appTheme: $carsStageGate.state.map((state) => state.appTheme),
  pageLength: $carsStageGate.state.map((state) => state.carsListProps?.pageLength || 25),
  carViewSettings: $carsStageGate.state.map((state) => state.carsListProps?.carViewSettings),
  baseUrl: $carsStageGate.state.map((state) => state.carsListProps?.getUrl),
  sorting: $sorting,
  filter: $filterValues,
  defaultFilterValues: $defaultFilterValues.state,
  permanentFilterValues: $permanentFilterValues.state,
  pagesLoaded: $pagesLoaded,
  city: $selectedCities,
  creditFilter: $creditFilterCountRequestData,
  firstLoadPage: $firstLoadPage,
  filterWithDefaultValue: $filterWithDefaultValue,
  filterDeclaration: $declaration,
  settingsRelatedFilter: $settingsRelatedFilter
})
export const $countRequestPossible = guard({
  source: countListRequestData,
  filter: combine($requestPossible, $creditFilterCountRequestData, (s, a) => s && !!a),
  target: fetchCarsOnCreditFormFx
})

/**
 * Сохраняем в УРЛ параметры сортировки и страницы
 */
sample({
  source: { sorting: $sorting, pagesLoaded: $pagesLoaded },
  target: propagateToUrlFx
})

/*Флаг первой загрузки страницы*/
$firstLoadPage.on(setFirstLoadPage, (_, state) => state)

/*Флаг, подмешивается ли предустанволенный фильтр или нет в url*/
$filterWithDefaultValue.on(setFilterWithDefaultValue, (_, state) => state)

$settingsRelatedFilter.on(setSettingsRelatedFilter, (_, state) => state)
$relatedFilter.on(setRelatedFilter, (_, state) => state)

$isLoadMoreCars.on(handleIsLoadMoreCars, (state, payload) => ({ ...state, isChanged: payload }))
